import React, {useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {ArrowLeft, Search} from "lucide-react";
import {getRkReturnDetails} from "../../network/externalApi";
import {formatLongDate} from "utils/dateFormat";
import RkAccountMenu from "../../components/RkAccountMenu/RkAccountMenu";
import styles from "./ViewReturnDetails.module.scss";

const ALL_CATEGORY = {id: "all", name: "All"};
const asArray = (value) => (Array.isArray(value) ? value : []);

const extractReturnRecord = (raw) => {
    const response = raw?.data?.response ?? raw?.data ?? {};
    if (Array.isArray(response)) return {ReturnItems: response};

    return (
        response?.Return ??
        response?.return ??
        response?.ReturnDetails ??
        response?.returnDetails ??
        response?.return_details ??
        response
    );
};

const extractReturnItems = (record) => asArray(record?.ReturnItems);

const pickProductNumber = (item) => item?.ProductNumber

const pickProductName = (item) => item?.ProductName ?? "";

const pickReturnedCount = (item) => {
    const value = item?.ReturnedCount ?? 0;
    const count = Number(value);
    return Number.isFinite(count) && count >= 0 ? count : 0;
};

const pickCategoryId = (item) => item?.SubCategoryId ?? null;

const pickCategoryName = (item) => item?.SubCategoryName ?? "";

const pickOutletName = (value) => value?.OutletName ?? "";

const pickDeliveryDate = (value) => value?.DeliveryDate ?? "";

const ViewReturnDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {orderId: paramOrderId} = useParams();
    const orderId = paramOrderId || location.state?.orderId;
    const issueOrder = location.state?.order || {};

    const [returnRecord, setReturnRecord] = useState(null);
    const [keyword, setKeyword] = useState("");
    const [selectedCategoryId, setSelectedCategoryId] = useState(ALL_CATEGORY.id);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        if (orderId == null) {
            setError("Missing order ID");
            setIsLoading(false);
            return undefined;
        }

        const loadReturn = async () => {
            setIsLoading(true);
            setError("");
            try {
                const response = await getRkReturnDetails({orderId});
                if (!cancelled) setReturnRecord(extractReturnRecord(response));
            } catch (requestError) {
                if (cancelled) return;
                setError(
                    requestError?.response?.data?.message ||
                    requestError?.message ||
                    "Failed to load return items"
                );
                setReturnRecord(null);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        loadReturn();
        return () => {
            cancelled = true;
        };
    }, [orderId]);

    const items = useMemo(() => extractReturnItems(returnRecord), [returnRecord]);

    const categories = useMemo(() => {
        const uniqueCategories = new Map();
        items.forEach((item) => {
            const id = pickCategoryId(item);
            const name = pickCategoryName(item);
            if (id != null && name && !uniqueCategories.has(String(id))) {
                uniqueCategories.set(String(id), {id, name});
            }
        });
        return [ALL_CATEGORY, ...uniqueCategories.values()];
    }, [items]);

    const visibleItems = useMemo(() => {
        const query = keyword.trim().toLowerCase();

        return items.filter((item) => {
            const matchesCategory =
                selectedCategoryId === ALL_CATEGORY.id ||
                String(pickCategoryId(item)) === String(selectedCategoryId);
            const matchesQuery =
                !query ||
                [pickProductName(item), pickProductNumber(item)].some((value) =>
                    String(value ?? "")
                        .toLowerCase()
                        .includes(query)
                );
            return matchesCategory && matchesQuery;
        });
    }, [items, keyword, selectedCategoryId]);

    const outletName =
        pickOutletName(returnRecord) ||
        location.state?.outletName ||
        pickOutletName(issueOrder) ||
        "Return details";
    const orderDate =
        pickDeliveryDate(returnRecord) ||
        location.state?.orderDate ||
        pickDeliveryDate(issueOrder);

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => navigate(-1)}
                    aria-label="Go back"
                >
                    <ArrowLeft size={18}/>
                </button>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>{outletName}</h1>
                    {orderDate ? (
                        <div className={styles.subtitle}>
                            {formatLongDate(orderDate) || orderDate}
                        </div>
                    ) : null}
                </div>
                <RkAccountMenu buttonClassName={styles.iconButton}/>
            </div>

            <main className={styles.content}>
                <label className={styles.searchRow}>
          <span className={styles.searchIcon} aria-hidden="true">
            <Search size={18}/>
          </span>
                    <span className={styles.srOnly}>Search return items</span>
                    <input
                        className={styles.searchInput}
                        type="search"
                        placeholder="Search by item or SKU number"
                        value={keyword}
                        onChange={(event) => setKeyword(event.target.value)}
                    />
                </label>

                {!isLoading && !error && items.length > 0 ? (
                    <div className={styles.chipRow} aria-label="Filter by category">
                        {categories.map((category) => {
                            const active = String(category.id) === String(selectedCategoryId);
                            return (
                                <button
                                    key={category.id}
                                    type="button"
                                    className={`${styles.chip} ${
                                        active ? styles.chipActive : ""
                                    }`}
                                    onClick={() => setSelectedCategoryId(category.id)}
                                >
                                    {category.name}
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                <div className={styles.productList} aria-busy={isLoading}>
                    {isLoading ? (
                        <div className={styles.stateText}>Loading return items…</div>
                    ) : error ? (
                        <div className={`${styles.stateText} ${styles.errorText}`}>
                            {error}
                        </div>
                    ) : visibleItems.length === 0 ? (
                        <div className={styles.stateText}>
                            {items.length === 0
                                ? "No returned items found"
                                : "No matching returned items found"}
                        </div>
                    ) : (
                        visibleItems.map((item, index) => {
                            const productNumber = pickProductNumber(item);
                            const productName = pickProductName(item);

                            return (
                                <div
                                    key={productNumber ?? index}
                                    className={styles.productCard}
                                >
                                    <div className={styles.productInfo}>
                                        <div className={styles.productName}>
                                            {productName || `Product ${productNumber ?? ""}`.trim()}
                                        </div>
                                        {productNumber != null ? (
                                            <div className={styles.productSku}>
                                                SKU: {productNumber}
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className={styles.returnedQuantity}>
                                        <strong>{pickReturnedCount(item)}</strong>
                                        <span>Returned qty</span>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </main>
        </div>
    );
};

export default ViewReturnDetails;
